import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading } from "../components/Layout";
import { BandBadge, StatusChip, LeadSourceBadge } from "../components/Badges";
import { STAGES, BAND } from "../lib/constants";
import { LayoutGrid, List, Clock, AlertTriangle, User, Upload, Plus } from "lucide-react";
import { NewLeadModal } from "../components/NewLeadModal";

const money = (v) =>
  !v || Number(v) <= 0 ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(v));

const daysLabel = (a) => (a && a.applies ? `${a.days_in_stage}d in stage` : null);

function ViewToggle({ view, setView }) {
  return (
    <div className="flex border border-[#DFDAD0]" data-testid="pipeline-view-toggle">
      {[
        ["kanban", "Kanban", LayoutGrid],
        ["list", "List", List],
      ].map(([v, label, Icon]) => (
        <button key={v} onClick={() => setView(v)} data-testid={`pipeline-view-${v}`}
          className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider transition-colors ${view === v ? "bg-[#1C2530] text-[#F5F3EE]" : "text-[#1C2530]/50 hover:bg-[#EFECE5]"}`}>
          <Icon className="w-3.5 h-3.5" /> {label}
        </button>
      ))}
    </div>
  );
}

function Card({ lead, dragging, canDrag }) {
  const nav = useNavigate();
  const aging = lead.stage_aging;
  const overdue = aging && aging.applies && aging.aging;
  return (
    <div
      onClick={() => nav(`/leads/${lead.id}`)}
      data-testid={`pipeline-card-${lead.id}`}
      className={`bg-white border p-2.5 cursor-pointer transition-all ${dragging ? "shadow-lg border-[#1C2530] rotate-1" : "border-[#DFDAD0] hover:border-[#1C2530]"} ${overdue ? "ring-1 ring-[#B23A2E]/40" : ""}`}
      style={{ borderLeftWidth: 3, borderLeftColor: BAND[lead.band] || "#94A3AD" }}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium text-[#1C2530] truncate">{lead.account?.name || "—"}</div>
        {!canDrag && <span title="Read-only" className="font-mono text-[8px] text-[#1C2530]/30 uppercase shrink-0">view</span>}
      </div>
      <div className="text-xs text-[#1C2530]/60 truncate">{lead.contact?.name}{lead.contact?.title ? ` · ${lead.contact.title}` : ""}</div>
      {lead.source && <div className="mt-1"><LeadSourceBadge source={lead.source} /></div>}
      <div className="flex items-center justify-between mt-2">
        <span className="font-mono text-xs tabular-nums text-[#1C2530]" data-testid={`pipeline-card-value-${lead.id}`}>{money(lead.account?.deal_value)}</span>
        <BandBadge band={lead.band} score={lead.total_score} />
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#DFDAD0]/60">
        <span className="flex items-center gap-1 font-mono text-[10px] text-[#1C2530]/55 truncate">
          <User className="w-3 h-3 shrink-0" />{lead.assignee_name || "Unassigned"}
        </span>
        {aging && aging.applies && (
          <span data-testid={`pipeline-card-aging-${lead.id}`}
            className={`flex items-center gap-1 font-mono text-[10px] shrink-0 ${overdue ? "text-[#B23A2E] font-semibold" : "text-[#1C2530]/45"}`}>
            {overdue ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
            {aging.days_in_stage}d{overdue ? ` / ${aging.limit_days}d` : ""}
          </span>
        )}
      </div>
    </div>
  );
}

export default function Pipeline() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState(null);
  const [config, setConfig] = useState({ labels: {}, approval_stages: [] });
  const [view, setView] = useState("kanban");
  const [modal, setModal] = useState(false);
  const [regions, setRegions] = useState([]);

  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => {
    load();
    api.get("/pipeline/config").then((r) => setConfig(r.data)).catch(() => {});
    api.get("/regions").then((r) => setRegions(r.data)).catch(() => {});
  }, []);

  const label = (s) => config.labels?.[s] || s;
  const canDrag = (l) => user.role === "admin" || user.role === "supervisor" || l.assigned_to === user.id;

  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) return;
    const target = destination.droppableId;
    const lead = leads.find((l) => l.id === draggableId);
    if (!lead) return;
    const prevStage = lead.stage;
    // optimistic
    setLeads((ls) => ls.map((l) => (l.id === draggableId ? { ...l, stage: target } : l)));
    try {
      const { data } = await api.post(`/leads/${draggableId}/stage`, { stage: target });
      if (data && data.pending_approval) {
        setLeads((ls) => ls.map((l) => (l.id === draggableId ? { ...l, stage: prevStage } : l)));
        toast.info(`"${label(target)}" needs approval — submitted for sign-off.`);
      } else {
        toast.success(`Moved to ${label(target)}`);
      }
    } catch (e) {
      setLeads((ls) => ls.map((l) => (l.id === draggableId ? { ...l, stage: prevStage } : l)));
      toast.error(apiError(e.response?.data?.detail) || "Could not move deal");
    }
  };

  if (!leads) return (<><PageHeader eyebrow="STAGE MACHINE" title="Pipeline" hint="All open leads by stage. Drag or use stage buttons to move; time limits and approval gates per stage are configured in Settings → Pipeline & Approvals." /><div className="p-8"><Loading /></div></>);

  if (leads.length === 0) return (
    <>
      <PageHeader eyebrow="STAGE MACHINE" title="Pipeline" hint="All open leads by stage. Drag or use stage buttons to move; time limits and approval gates per stage are configured in Settings → Pipeline & Approvals." />
      <div className="flex flex-col items-center justify-center text-center px-6 py-24" data-testid="pipeline-empty">
        <div className="w-14 h-14 rounded-full bg-[#3E8F99]/10 flex items-center justify-center mb-5">
          <LayoutGrid className="w-6 h-6 text-[#3E8F99]" strokeWidth={1.5} />
        </div>
        <h2 className="text-2xl font-light text-[#1C2530] mb-2">Your pipeline is empty</h2>
        <p className="text-base text-[#1C2530]/55 max-w-md mb-8">Bring your contacts in and they'll flow through your stages here. Import a list to get started — new leads land in Suspect, ready for research.</p>
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/import")} data-testid="pipeline-empty-import"
            className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-5 py-2.5 text-sm rounded-full hover:bg-[#2a3644] transition-colors"><Upload className="w-4 h-4" /> Import leads</button>
          <button onClick={() => setModal(true)} data-testid="pipeline-empty-add"
            className="flex items-center gap-2 border border-[#DFDAD0] px-5 py-2.5 text-sm rounded-full hover:bg-[#EFECE5] transition-colors"><Plus className="w-4 h-4" /> Add a lead</button>
        </div>
      </div>
      {modal && <NewLeadModal regions={regions} onClose={() => setModal(false)} onCreated={load} />}
    </>
  );

  const open = leads.filter((l) => l.status === "open");
  const cols = STAGES.map((s) => ({ stage: s, items: open.filter((l) => l.stage === s) }));

  return (
    <>
      <PageHeader eyebrow="STAGE MACHINE" title="Pipeline" hint="All open leads by stage. Drag or use stage buttons to move; time limits and approval gates per stage are configured in Settings → Pipeline & Approvals."
        right={<div className="flex items-center gap-3"><span className="font-mono text-xs text-[#1C2530]/50">{open.length} OPEN</span><ViewToggle view={view} setView={setView} /><button onClick={() => setModal(true)} data-testid="new-lead-btn" className="flex items-center gap-1.5 bg-[#E50914] text-white px-4 py-2 text-sm font-medium rounded-sm hover:bg-[#c40810]"><Plus className="w-4 h-4" /> New Lead</button></div>} />
      {modal && <NewLeadModal regions={regions} onClose={() => setModal(false)} onCreated={load} />}
      {view === "kanban" ? (
        <div className="p-6 overflow-x-auto">
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="flex gap-3 min-w-max" data-testid="pipeline-kanban-board">
              {cols.map((c) => {
                const approval = config.approval_stages?.includes(c.stage);
                return (
                  <div key={c.stage} className="w-64 shrink-0" data-testid={`pipeline-col-${c.stage}`}>
                    <div className="px-3 py-2 border border-[#DFDAD0] bg-[#EFECE5] flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/70 truncate" title={c.stage}>{label(c.stage)}</span>
                      <span className="flex items-center gap-1.5">
                        {approval && <span title="Moving here requires approval" data-testid={`pipeline-col-approval-${c.stage}`} className="font-mono text-[8px] uppercase text-[#E2711D]">approval</span>}
                        <span className="font-mono text-[10px] tabular-nums text-[#1C2530]/50">{c.items.length}</span>
                      </span>
                    </div>
                    <Droppable droppableId={c.stage}>
                      {(prov, snap) => (
                        <div ref={prov.innerRef} {...prov.droppableProps}
                          className={`p-2 space-y-2 min-h-[140px] border border-t-0 border-[#DFDAD0] transition-colors ${snap.isDraggingOver ? "bg-[#3E8F99]/10" : "bg-[#F5F3EE]"}`}>
                          {c.items.length === 0 && !snap.isDraggingOver && (
                            <div className="font-mono text-[10px] text-[#1C2530]/25 text-center py-6">—</div>
                          )}
                          {c.items.map((l, i) => (
                            <Draggable key={l.id} draggableId={l.id} index={i} isDragDisabled={!canDrag(l)}>
                              {(dp, ds) => (
                                <div ref={dp.innerRef} {...dp.draggableProps} {...dp.dragHandleProps}>
                                  <Card lead={l} dragging={ds.isDragging} canDrag={canDrag(l)} />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {prov.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        </div>
      ) : (
        <div className="p-6">
          <div className="border border-[#DFDAD0] bg-white overflow-x-auto" data-testid="pipeline-list-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#EFECE5] border-b border-[#DFDAD0] text-left font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">
                  <th className="px-3 py-2">Company</th>
                  <th className="px-3 py-2">Contact</th>
                  <th className="px-3 py-2">Stage</th>
                  <th className="px-3 py-2 text-right">Value</th>
                  <th className="px-3 py-2">Owner</th>
                  <th className="px-3 py-2 text-right">Days in stage</th>
                  <th className="px-3 py-2">Band</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#DFDAD0]">
                {open.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-8 text-center text-[#1C2530]/40">No open deals.</td></tr>
                )}
                {open.map((l) => {
                  const a = l.stage_aging;
                  const overdue = a && a.applies && a.aging;
                  return (
                    <tr key={l.id} onClick={() => nav(`/leads/${l.id}`)} data-testid={`pipeline-row-${l.id}`}
                      className="hover:bg-[#EFECE5]/60 cursor-pointer">
                      <td className="px-3 py-2 font-medium text-[#1C2530]"><span className="inline-flex items-center gap-2">{l.account?.name || "—"}<LeadSourceBadge source={l.source} /></span></td>
                      <td className="px-3 py-2 text-[#1C2530]/70">{l.contact?.name || "—"}</td>
                      <td className="px-3 py-2"><span className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/70">{label(l.stage)}</span></td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{money(l.account?.deal_value)}</td>
                      <td className="px-3 py-2 text-[#1C2530]/70">{l.assignee_name || "Unassigned"}</td>
                      <td className={`px-3 py-2 text-right font-mono tabular-nums ${overdue ? "text-[#B23A2E] font-semibold" : "text-[#1C2530]/55"}`}>
                        {a && a.applies ? (overdue ? `${a.days_in_stage} / ${a.limit_days}` : a.days_in_stage) : "—"}
                        {overdue && <AlertTriangle className="inline w-3 h-3 ml-1 -mt-0.5" />}
                      </td>
                      <td className="px-3 py-2"><BandBadge band={l.band} score={l.total_score} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
